import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Sidebar } from "@/components/Sidebar";
import Dashboard from "@/pages/Dashboard";
import MapPage from "@/pages/MapPage";
import Pals from "@/pages/Pals";
import Passives from "@/pages/Passives";
import Settings from "@/pages/Settings";
import SharedMap from "@/pages/SharedMap";
import SharedPlan from "@/pages/SharedPlan";
import Login from "@/pages/Login";
import AdminLogin from "@/pages/AdminLogin";
import AuthCallback from "@/pages/AuthCallback";
import Discord from "@/pages/Discord";
import Leaderboard from "@/pages/Leaderboard";
import ResetPassword from "@/pages/ResetPassword";
import Breeding from "@/pages/Breeding";
import { PalDetailProvider } from "@/components/PalDetailModal";
import { PalCompareProvider } from "@/components/PalCompareModal";

function AdminRoute({ children }) {
  const { isAdmin } = useAuth();
  return isAdmin ? children : <Navigate to="/map" replace />;
}

function Shell() {
  const { isAdmin } = useAuth();
  return (
    <PalCompareProvider>
    <PalDetailProvider>
      <Sidebar />
      <main className="md:pl-60 pt-14 md:pt-0 pb-20 md:pb-0">
        <div className="p-4 sm:p-8 max-w-[1500px]">
          <Routes>
            <Route path="/" element={isAdmin ? <Dashboard /> : <Navigate to="/map" replace />} />
            <Route path="/map" element={<MapPage />} />
            <Route path="/pals" element={<Pals />} />
            <Route path="/passives" element={<Passives />} />
            <Route path="/breeding" element={<Breeding />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/discord" element={<Discord />} />
            <Route path="/settings" element={<AdminRoute><Settings /></AdminRoute>} />
            <Route path="*" element={<Navigate to="/map" replace />} />
          </Routes>
        </div>
      </main>
    </PalDetailProvider>
    </PalCompareProvider>
  );
}

function getQueryParam(name) {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(name);
}

function Gate() {
  const { user } = useAuth();

  // Password reset link: /?reset_token=...
  const resetToken = getQueryParam("reset_token");
  if (resetToken) return <ResetPassword token={resetToken} />;

  // Emergent Google Auth returns to "/" with #session_id=... — let AuthCallback finish it.
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center text-primary" data-testid="auth-loading">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }
  if (!user) {
    if (typeof window !== "undefined" && window.location.pathname === "/admin-login") {
      return <AdminLogin />;
    }
    return <Login />;
  }
  return <Shell />;
}

function App() {
  return (
    <div className="App min-h-screen">
      <BrowserRouter>
        <Toaster theme="dark" position="top-center" richColors />
        <AuthProvider>
          <Routes>
            <Route path="/share/:id" element={<SharedMap />} />
            <Route path="/plan/:id" element={<SharedPlan />} />
            <Route path="/*" element={<Gate />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
